import React from 'react';
import { useTranslation } from 'react-i18next';
import { interestClusterTitle } from '../../utils/interestClusterDisplay';

function StatCard({ label, value }) {
  return (
    <div className="rounded-lg border border-edge bg-surface-muted px-3 py-2.5">
      <p className="text-xs font-medium text-ink-muted">{label}</p>
      <p className="mt-0.5 text-lg font-semibold text-ink tabular-nums">{value}</p>
    </div>
  );
}

/**
 * Presenter-only analytics strip for /test-results.
 * @param {{ analytics: object | null, loading?: boolean }} props
 */
export default function TestResultsAnalyticsSummary({ analytics, loading = false }) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="mt-6 rounded-lg border border-edge bg-surface p-4 text-sm text-ink-muted">
        {t('testResultsPage.analyticsLoading')}
      </div>
    );
  }

  if (!analytics || analytics.total === 0) {
    return (
      <div className="mt-6 rounded-lg border border-edge bg-surface p-4 text-sm text-ink-muted">
        {t('testResultsPage.analyticsEmpty')}
      </div>
    );
  }

  const {
    total,
    uniqueUsers,
    interestCount,
    abilityCount,
    last7Days,
    last30Days,
    submittedCount,
    timedOutCount,
    choiceTotals,
    topClusters,
    abilityBySlug,
    abilityAvgScore,
    abilityScoredCount
  } = analytics;

  const choiceTotal =
    (choiceTotals?.like || 0) + (choiceTotals?.neutral || 0) + (choiceTotals?.dislike || 0);

  return (
    <div className="mt-6 space-y-4 rounded-lg border border-edge bg-surface p-4 sm:p-5">
      <div>
        <h2 className="text-sm font-semibold text-ink">
          {t('testResultsPage.analyticsOverviewHeading')}
        </h2>
        <p className="mt-0.5 text-xs text-ink-muted">
          {t('testResultsPage.analyticsOverviewHint')}
        </p>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          <StatCard label={t('testResultsPage.analyticsTotal')} value={total} />
          <StatCard label={t('testResultsPage.analyticsUniqueUsers')} value={uniqueUsers} />
          <StatCard label={t('testResultsPage.analyticsInterest')} value={interestCount} />
          <StatCard label={t('testResultsPage.analyticsAbility')} value={abilityCount} />
          <StatCard label={t('testResultsPage.analyticsLast7')} value={last7Days} />
          <StatCard label={t('testResultsPage.analyticsLast30')} value={last30Days} />
        </div>
        {(submittedCount > 0 || timedOutCount > 0) && (
          <p className="mt-2 text-xs text-ink-subtle">
            {t('testResultsPage.analyticsCompletionReasons', {
              submitted: submittedCount,
              timedOut: timedOutCount
            })}
          </p>
        )}
      </div>

      <div className="grid gap-4 border-t border-edge pt-4 lg:grid-cols-2">
        <div>
          <h3 className="text-sm font-semibold text-ink">
            {t('testResultsPage.analyticsInterestInsights')}
          </h3>
          {interestCount === 0 ? (
            <p className="mt-2 text-xs text-ink-muted">{t('testResultsPage.analyticsNoInterest')}</p>
          ) : (
            <>
              {choiceTotal > 0 ? (
                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                  <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-emerald-900">
                    {t('testResultsPage.choiceLike')}: {choiceTotals.like}
                  </span>
                  <span className="rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-sky-900">
                    {t('testResultsPage.choiceNeutral')}: {choiceTotals.neutral}
                  </span>
                  <span className="rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-rose-900">
                    {t('testResultsPage.choiceDislike')}: {choiceTotals.dislike}
                  </span>
                </div>
              ) : null}
              {topClusters.length > 0 ? (
                <div className="mt-3">
                  <p className="text-xs font-medium text-ink-muted mb-1.5">
                    {t('testResultsPage.analyticsTopClusters')}
                  </p>
                  <ul className="space-y-1">
                    {topClusters.map((row) => (
                      <li
                        key={row.cluster_id}
                        className="flex items-center justify-between gap-2 text-sm text-ink"
                      >
                        <span className="min-w-0 truncate">
                          {interestClusterTitle(row, t)}
                        </span>
                        <span className="shrink-0 tabular-nums text-ink-muted text-xs">
                          {t('testResultsPage.analyticsClusterCount', { count: row.count })}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="mt-2 text-xs text-ink-muted">
                  {t('testResultsPage.analyticsNoClusters')}
                </p>
              )}
            </>
          )}
        </div>

        <div>
          <h3 className="text-sm font-semibold text-ink">
            {t('testResultsPage.analyticsAbilityInsights')}
          </h3>
          {abilityCount === 0 ? (
            <p className="mt-2 text-xs text-ink-muted">{t('testResultsPage.analyticsNoAbility')}</p>
          ) : (
            <>
              {abilityScoredCount > 0 ? (
                <p className="mt-2 text-sm text-ink">
                  {t('testResultsPage.analyticsAvgScore', {
                    score: abilityAvgScore.toFixed(1),
                    count: abilityScoredCount
                  })}
                </p>
              ) : null}
              {abilityBySlug.length > 0 ? (
                <div className="mt-3">
                  <p className="text-xs font-medium text-ink-muted mb-1.5">
                    {t('testResultsPage.analyticsByPaper')}
                  </p>
                  <ul className="space-y-1">
                    {abilityBySlug.map((row) => (
                      <li
                        key={row.slug}
                        className="flex items-center justify-between gap-2 text-sm text-ink"
                      >
                        <span className="min-w-0 truncate font-mono text-xs">{row.slug}</span>
                        <span className="shrink-0 tabular-nums text-ink-muted text-xs">
                          {row.count}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
